import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button, Box, Grid, Stack, Typography, TextField, MenuItem, Chip,
  Divider, Alert, FormControlLabel, Checkbox, CircularProgress, Collapse,
  Avatar, IconButton, Tooltip, Paper,
} from '@mui/material';
import {
  Phone, MessageCircle, Mail, Send, Clock, UserCheck,
  Copy, Check, Building2, History, Eye, UserX, Trash2,
} from 'lucide-react';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';
import {
  saveFollowUp, logFollowUpContact, sendFollowUpEmail, sendFollowUpWhatsApp, recordFollowUpOpen,
  removeFollowUp, deleteFollowUpUser,
  FOLLOWUP_STATUSES, STATUS_COLORS, FOLLOWUP_REASONS, SEGMENT_LABEL, SEGMENT_COLORS, labelOf,
} from '../utils/followUpApi';

// Format phone for wa.me / tel target
const waNumber = (phone) => {
  let digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
  return digits.length === 10 ? `91${digits}` : digits;
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return String(d);
  }
};

const getInitials = (name) => {
  if (!name || name === '—') return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0][0].toUpperCase();
};

export default function FollowUpModal({ open, user, onClose, onSaved }) {
  const navigate = useNavigate();
  const fu = user?.followup || {};
  const [status, setStatus] = useState(fu.status || 'pending');
  const [reasonCategory, setReasonCategory] = useState(fu.reason_category || '');
  const [reason, setReason] = useState('');
  const [nextAt, setNextAt] = useState(fu.next_follow_up_at ? fu.next_follow_up_at.slice(0, 10) : '');
  const [markContacted, setMarkContacted] = useState(false);
  const [contactMethod, setContactMethod] = useState(fu.contact_method || 'none');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [copied, setCopied] = useState(false);

  // Email composer state
  const [emailOpen, setEmailOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // WhatsApp direct composer state
  const [waOpen, setWaOpen] = useState(false);
  const [waMessage, setWaMessage] = useState('');
  const [waSending, setWaSending] = useState(false);

  // Open / view count tracking
  const [openCount, setOpenCount] = useState(fu.open_count || 0);
  const [lastOpenedAt, setLastOpenedAt] = useState(fu.last_opened_at || null);

  // Local activity list for instant UI updates
  const [activities, setActivities] = useState(fu.activities || []);

  // Re-seed state and record view count when modal opens
  useEffect(() => {
    if (user) {
      const ufu = user.followup || {};
      setStatus(ufu.status || 'pending');
      setReasonCategory(ufu.reason_category || '');
      setReason('');
      setNextAt(ufu.next_follow_up_at ? ufu.next_follow_up_at.slice(0, 10) : '');
      setMarkContacted(false);
      setContactMethod(ufu.contact_method || 'none');
      setFeedback(null);
      setEmailOpen(false);
      setSubject('');
      setMessage('');
      setWaOpen(false);
      setWaMessage('');
      setActivities(ufu.activities || []);
      setOpenCount(ufu.open_count || 0);
      setLastOpenedAt(ufu.last_opened_at || null);

      if (open && user._id) {
        recordFollowUpOpen(user._id)
          .then((res) => {
            if (typeof res?.data?.open_count === 'number') {
              setOpenCount(res.data.open_count);
              setLastOpenedAt(res.data.last_opened_at);
              if (user.followup) {
                user.followup.open_count = res.data.open_count;
                user.followup.last_opened_at = res.data.last_opened_at;
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [user, open]);

  if (!user) return null;

  const personName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const name = personName || user.business_name || 'Unnamed user';
  const waContactName = personName || user.business_name || '';
  const seg = SEGMENT_COLORS[user.segment] || SEGMENT_COLORS.active;
  const currentStatusObj = STATUS_COLORS[status] || STATUS_COLORS.pending;
  const wa = waNumber(user.phone);

  const handleCopyPhone = () => {
    if (user.phone) {
      navigator.clipboard.writeText(String(user.phone));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleQuickContact = async (method, actionUrl) => {
    try {
      const { data } = await logFollowUpContact(user._id, method, `Quick touch via ${method.toUpperCase()}`);
      if (data?.followup?.activities) {
        setActivities(data.followup.activities);
      }
      if (user.followup) {
        user.followup.last_contacted_at = data?.followup?.last_contacted_at || new Date().toISOString();
        user.followup.contact_method = method;
        user.followup.contact_count = data?.followup?.contact_count || (user.followup.contact_count || 0) + 1;
        if (!user.followup.status || user.followup.status === 'pending') {
          user.followup.status = method === 'call' ? 'attempted' : 'contacted';
          setStatus(user.followup.status);
        }
      }
      setFeedback({ severity: 'success', message: `Logged ${method.toUpperCase()} touch.` });
      onSaved && onSaved();
    } catch {
      // Global axios handler handles error notifications
    }
    if (actionUrl) {
      if (method === 'call') {
        window.open(actionUrl, '_self');
      } else if (method === 'whatsapp') {
        onClose && onClose();
        navigate(actionUrl);
      } else {
        window.open(actionUrl, '_blank', 'noopener');
      }
    }
  };

  const handleQuickDateShortcut = (daysToAdd) => {
    const d = new Date();
    d.setDate(d.getDate() + daysToAdd);
    const iso = d.toISOString().slice(0, 10);
    setNextAt(iso);
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const shouldMarkContacted =
        markContacted ||
        (status && status !== 'pending') ||
        Boolean(reason.trim()) ||
        Boolean(reasonCategory);
      const effectiveMethod = contactMethod && contactMethod !== 'none' ? contactMethod : 'call';

      const { data } = await saveFollowUp(user._id, {
        status,
        reason_category: reasonCategory || null,
        reason: reason.trim() || undefined,
        contact_method: shouldMarkContacted ? effectiveMethod : (contactMethod || 'none'),
        next_follow_up_at: nextAt || null,
        mark_contacted: shouldMarkContacted,
      });
      if (data?.followup?.activities) {
        setActivities(data.followup.activities);
      }
      if (user.followup) {
        user.followup.status = status;
        user.followup.reason_category = reasonCategory || null;
        if (reason.trim()) user.followup.reason = reason.trim();
        if (shouldMarkContacted) {
          user.followup.last_contacted_at = data?.followup?.last_contacted_at || new Date().toISOString();
          user.followup.contact_method = effectiveMethod;
          user.followup.contact_count = data?.followup?.contact_count || (user.followup.contact_count || 0) + 1;
        }
      }
      setFeedback({ severity: 'success', message: 'Follow-up saved successfully.' });
      setReason('');
      setMarkContacted(false);
      onSaved && onSaved();
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.response?.data?.msg || 'Could not save follow-up.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm(`Are you sure you want to remove ${name} from follow-up?`)) return;
    setRemoving(true);
    setFeedback(null);
    try {
      await removeFollowUp(user._id);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.response?.data?.msg || 'Could not remove from follow-up.' });
    } finally {
      setRemoving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete ${name} from the user table? All their associated records will be removed. This action cannot be undone.`)) return;
    setDeleting(true);
    setFeedback(null);
    try {
      await deleteFollowUpUser(user._id);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.response?.data?.msg || 'Could not delete user from database.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!message.trim()) {
      setFeedback({ severity: 'error', message: 'Email message is required.' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await sendFollowUpEmail(user._id, subject, message);
      setFeedback({ severity: 'success', message: data?.msg || 'Email sent successfully.' });
      setEmailOpen(false);
      setSubject('');
      setMessage('');
      onSaved && onSaved();
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.response?.data?.msg || 'Could not send email.' });
    } finally {
      setSending(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!waMessage.trim()) {
      setFeedback({ severity: 'error', message: 'WhatsApp message is required.' });
      return;
    }
    setWaSending(true);
    setFeedback(null);
    try {
      const { data } = await sendFollowUpWhatsApp(user._id, waMessage.trim());
      if (data?.followup?.activities) {
        setActivities(data.followup.activities);
      }
      setFeedback({ severity: 'success', message: data?.msg || 'WhatsApp message sent successfully.' });
      setWaOpen(false);
      setWaMessage('');
      onSaved && onSaved();
    } catch (e) {
      setFeedback({ severity: 'error', message: e?.response?.data?.msg || 'Could not send WhatsApp message.' });
    } finally {
      setWaSending(false);
    }
  };

  // Check if scheduled date is overdue
  const isOverdue = nextAt && new Date(nextAt + 'T23:59:59') < new Date() && !['converted', 'invalid', 'not_interested'].includes(status);
  const isToday = nextAt && new Date().toISOString().slice(0, 10) === nextAt;

  return (
    <AdminDialog open={open} onClose={onClose} maxWidth="md">
      <AdminDialogHeader
        title={`Follow-up · ${name}`}
        subtitle="Manage lead status, schedule touches, and track complete communication history."
        icon={UserCheck}
        onClose={onClose}
      />
      <AdminDialogContent dividers>
        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        {/* Lead Profile Summary Card */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
            borderColor: 'divider',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: 'primary.main',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                }}
              >
                {getInitials(name)}
              </Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {name}
                  </Typography>
                  <Chip
                    size="small"
                    label={SEGMENT_LABEL[user.segment] || user.segment}
                    sx={{ bgcolor: seg.bg, color: seg.fg, fontWeight: 700, height: 22 }}
                  />
                  <Chip
                    size="small"
                    label={user.userType || 'Buyer'}
                    variant="outlined"
                    sx={{ height: 22, fontWeight: 600 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Joined: {fmtDateTime(user.created_at)}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={labelOf(FOLLOWUP_STATUSES, status)}
                sx={{
                  bgcolor: currentStatusObj.bg,
                  color: currentStatusObj.fg,
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                }}
              />
              {fu.contact_count > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${fu.contact_count} touch${fu.contact_count > 1 ? 'es' : ''}`}
                  sx={{ fontWeight: 600, height: 24 }}
                />
              )}
              <Tooltip title={lastOpenedAt ? `Last opened: ${fmtDateTime(lastOpenedAt)}` : 'First opened just now'}>
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<Eye size={13} color="#637381" />}
                  label={`Opened ${openCount || 1}x`}
                  sx={{ fontWeight: 600, height: 24, color: 'text.secondary', borderColor: 'divider' }}
                />
              </Tooltip>
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {/* Contact Details Grid */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Phone Number
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user.phone ? `+91 ${user.phone}` : '—'}
                </Typography>
                {user.phone && (
                  <Tooltip title={copied ? 'Copied!' : 'Copy Phone'}>
                    <IconButton size="small" onClick={handleCopyPhone} sx={{ p: 0.5 }}>
                      {copied ? <Check size={14} color="#2e7d32" /> : <Copy size={14} color="#637381" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Email Address
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {user.email || '—'}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Business Listing
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Building2 size={14} color="#637381" />
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                  {user.business_name || (user.has_business ? 'Business created' : 'No business yet')}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* Quick Communication Actions Bar */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            color="inherit"
            startIcon={<Phone size={15} />}
            disabled={!user.phone}
            onClick={() => handleQuickContact('call', `tel:+${wa}`)}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: 'action.selected',
              '&:hover': { bgcolor: 'action.focus' },
            }}
          >
            Call
          </Button>

          <Button
            size="small"
            variant={waOpen ? 'contained' : 'outlined'}
            startIcon={<MessageCircle size={15} />}
            disabled={!wa}
            onClick={() => {
              setWaOpen((v) => !v);
              if (!waOpen && !waMessage) {
                const greetingName = (user.first_name || '').trim() || (user.business_name || '').trim() || 'there';
                setWaMessage(
                  `Hi ${greetingName}, you have active buyer requirements on Reseller Bazzar waiting for your quotation. View details and contact buyers here: https://www.resellerbazzar.in/profile/${user._id}?tab=leads`
                );
              }
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              borderColor: '#25D366',
              color: waOpen ? '#fff' : '#128C7E',
              bgcolor: waOpen ? '#25D366' : 'transparent',
              '&:hover': { bgcolor: '#25D366', color: '#fff', borderColor: '#25D366' },
            }}
          >
            {waOpen ? 'Close WhatsApp' : 'Send WhatsApp'}
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<MessageCircle size={15} />}
            disabled={!wa}
            onClick={() => {
              const greetingName = (user.first_name || '').trim() || (user.business_name || '').trim() || 'there';
              handleQuickContact(
                'whatsapp',
                `/whatsapp?phone=${encodeURIComponent(wa)}&name=${encodeURIComponent(waContactName)}&text=${encodeURIComponent(
                  `Hi ${greetingName}, this is from Reseller Bazzar. We noticed you started registering on our platform and wanted to see if you needed any help completing your setup!`
                )}`
              );
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: '#25D366',
              color: '#fff',
              '&:hover': { bgcolor: '#1ebe5d' },
            }}
          >
            Open Chat
          </Button>

          <Button
            size="small"
            variant={emailOpen ? 'contained' : 'outlined'}
            color="primary"
            startIcon={<Mail size={15} />}
            disabled={!user.email}
            onClick={() => setEmailOpen((v) => !v)}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            {emailOpen ? 'Close Email' : 'Send Email'}
          </Button>

          <Stack direction="row" spacing={1} sx={{ ml: 'auto' }} alignItems="center" flexWrap="wrap">
            {fu.last_contacted_at && (
              <Chip
                size="small"
                icon={<Clock size={13} />}
                label={`Last touch: ${fmtDateTime(fu.last_contacted_at)} (${fu.contact_method || 'call'})`}
                sx={{ height: 26 }}
              />
            )}
            <Chip
              size="small"
              icon={<Eye size={13} />}
              label={`Opened: ${openCount || 1}x`}
              sx={{ height: 26, bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.75rem' }}
            />
          </Stack>
        </Stack>

        {/* Collapsible WhatsApp Composer */}
        <Collapse in={waOpen}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: '#25D366',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#128C7E' }}>
              Send Direct WhatsApp Message to +{wa}
            </Typography>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Quick Templates:
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label="Leads Follow-Up"
                clickable
                onClick={() => {
                  const greetingName = (user.first_name || '').trim() || (user.business_name || '').trim() || 'there';
                  setWaMessage(
                    `Hi ${greetingName}, you have active buyer requirements on Reseller Bazzar waiting for your quotation. View details and contact buyers here: https://www.resellerbazzar.in/profile/${user._id}?tab=leads`
                  );
                }}
                sx={{ borderRadius: 1.5 }}
              />
              <Chip
                size="small"
                label="Onboarding Help"
                clickable
                onClick={() => {
                  const greetingName = (user.first_name || '').trim() || (user.business_name || '').trim() || 'there';
                  setWaMessage(
                    `Hi ${greetingName}, Team Reseller Bazzar here. We noticed you haven't completed your profile setup. Need any help with your registration or catalogue?`
                  );
                }}
                sx={{ borderRadius: 1.5 }}
              />
              <Chip
                size="small"
                label="Upload Products"
                clickable
                onClick={() => {
                  const greetingName = (user.first_name || '').trim() || (user.business_name || '').trim() || 'there';
                  setWaMessage(
                    `Hi ${greetingName}, your business profile is live on Reseller Bazzar! Upload your product catalog now to start receiving high-intent buyer inquiries directly.`
                  );
                }}
                sx={{ borderRadius: 1.5 }}
              />
            </Stack>

            <TextField
              fullWidth
              size="small"
              label="WhatsApp Message"
              multiline
              minRows={3}
              value={waMessage}
              onChange={(e) => setWaMessage(e.target.value)}
              placeholder="Type WhatsApp message here…"
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => setWaOpen(false)} sx={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={waSending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
                disabled={waSending}
                onClick={handleSendWhatsApp}
                sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#1ebe5d' } }}
              >
                Send WhatsApp Now
              </Button>
            </Stack>
          </Paper>
        </Collapse>

        {/* Collapsible Email Composer */}
        <Collapse in={emailOpen}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'primary.light',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
              Compose Direct Email to {user.email}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Welcome to Reseller Bazzar — Setup assistance"
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label="Message"
              multiline
              minRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a clear, helpful message to the user…"
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Button size="small" onClick={() => setEmailOpen(false)} sx={{ fontWeight: 600 }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <Send size={15} />}
                disabled={sending}
                onClick={handleSendEmail}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Send Email Now
              </Button>
            </Stack>
          </Paper>
        </Collapse>

        <Divider sx={{ mb: 2.5 }} />

        {/* Follow-up State Form */}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          Update Status & Schedule
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Follow-up status"
              value={status}
              onChange={(e) => {
                const val = e.target.value;
                setStatus(val);
                if (val && val !== 'pending') {
                  setMarkContacted(true);
                }
              }}
            >
              {FOLLOWUP_STATUSES.map((s) => (
                <MenuItem key={s.key} value={s.key}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: STATUS_COLORS[s.key]?.fg || '#616161',
                      }}
                    />
                    <span>{s.label}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Reason (blocker / objection)"
              value={reasonCategory}
              onChange={(e) => setReasonCategory(e.target.value)}
            >
              <MenuItem value="">
                <em>— None / Normal engagement —</em>
              </MenuItem>
              {FOLLOWUP_REASONS.map((r) => (
                <MenuItem key={r.key} value={r.key}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="Next Follow-up Date"
              slotProps={{ inputLabel: { shrink: true } }}
              InputLabelProps={{ shrink: true }}
              value={nextAt}
              onChange={(e) => setNextAt(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              helperText={
                isOverdue ? (
                  <span style={{ color: '#d32f2f', fontWeight: 600 }}>⚠️ This follow-up is overdue!</span>
                ) : isToday ? (
                  <span style={{ color: '#ed6c02', fontWeight: 600 }}>⏰ Scheduled for today!</span>
                ) : (
                  'Pick a reminder date'
                )
              }
            />
            {/* Quick date shortcuts */}
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                label="Tomorrow"
                clickable
                onClick={() => handleQuickDateShortcut(1)}
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
              <Chip
                size="small"
                label="+3 Days"
                clickable
                onClick={() => handleQuickDateShortcut(3)}
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
              <Chip
                size="small"
                label="Next Week"
                clickable
                onClick={() => handleQuickDateShortcut(7)}
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
              {nextAt && (
                <Chip
                  size="small"
                  label="Clear"
                  variant="outlined"
                  clickable
                  onClick={() => setNextAt('')}
                  sx={{ height: 22, fontSize: '0.75rem' }}
                />
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={markContacted || (status && status !== 'pending')}
                  onChange={(e) => setMarkContacted(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Mark as contacted now
                </Typography>
              }
            />
            {(markContacted || (status && status !== 'pending')) && (
              <TextField
                select
                fullWidth
                size="small"
                label="Contact method used"
                value={contactMethod !== 'none' ? contactMethod : 'call'}
                onChange={(e) => setContactMethod(e.target.value)}
                sx={{ mt: 1 }}
              >
                <MenuItem value="call">Phone Call</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </TextField>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Add New Follow-up Note"
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What did the customer say? What are the next action items?"
            />
          </Grid>
        </Grid>

        {/* Interaction History & Activity Timeline */}
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <History size={16} color="#637381" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Activity History & Timeline ({activities.length})
            </Typography>
          </Stack>

          {activities.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              {fu.reason ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Previous Working Note ({fmtDateTime(fu.updated_at || fu.last_contacted_at)})
                  </Typography>
                  <Typography variant="body2">{fu.reason}</Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No interaction history recorded yet. Add notes or log calls above.
                </Typography>
              )}
            </Paper>
          ) : (
            <Stack spacing={1.25} sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.5 }}>
              {[...activities].reverse().map((act, idx) => {
                const actDate = fmtDateTime(act.created_at);
                const isCall = act.action === 'call';
                const isWa = act.action === 'whatsapp';
                const isMail = act.action === 'email';
                const isStatus = act.action === 'status_change';

                return (
                  <Paper
                    key={act._id || idx}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.75,
                      bgcolor: 'background.paper',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isCall ? (
                          <Phone size={14} color="#f75d34" />
                        ) : isWa ? (
                          <MessageCircle size={14} color="#25D366" />
                        ) : isMail ? (
                          <Mail size={14} color="#0288d1" />
                        ) : isStatus ? (
                          <UserCheck size={14} color="#ed6c02" />
                        ) : (
                          <Clock size={14} color="#637381" />
                        )}
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {act.action.replace('_', ' ')}
                        </Typography>
                        {act.created_by && (
                          <Typography variant="caption" color="text.secondary">
                            by {act.created_by}
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {actDate}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', fontSize: '0.8125rem' }}>
                      {act.note || 'Updated lead'}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Box>
      </AdminDialogContent>
      <AdminDialogActions>
        <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
          <Button
            color="warning"
            variant="outlined"
            startIcon={removing ? <CircularProgress size={14} color="inherit" /> : <UserX size={15} />}
            disabled={removing || saving || deleting}
            onClick={handleRemove}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {removing ? 'Removing...' : 'Remove from Follow-up'}
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Trash2 size={15} />}
            disabled={removing || saving || deleting}
            onClick={handleDeleteUser}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            {deleting ? 'Deleting User...' : 'Delete User'}
          </Button>
        </Stack>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 600 }}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || removing}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ px: 3, fontWeight: 700 }}
        >
          Save Changes
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
}
